import { createContext, useState, useCallback, useContext } from "react";
import { loadPathsAsync as fetchPathsFromApi } from "@/api/paths";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PathSummary {
  id: string | number;
  name: string;
  rankName: string;
  activated: boolean;
  totalPoints: number;
}

export interface PathsContextValue {
  allPathNames: string[];
  allPaths: PathSummary[];
  isPathsLoading: boolean;
  isPathsLoaded: boolean;
  loadPathsAsync: (forceRefresh?: boolean) => Promise<string[]>;
  invalidatePathsCache: () => void;
  updatePathActivation: (id: string | number, activated: boolean) => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

// eslint-disable-next-line react-refresh/only-export-components
export const PathsContext = createContext<PathsContextValue>({
  allPathNames: [],
  allPaths: [],
  isPathsLoading: false,
  isPathsLoaded: false,
  loadPathsAsync: async () => [],
  invalidatePathsCache: () => {},
  updatePathActivation: () => {},
});

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const PathsProvider = ({ children }: { children: React.ReactNode }) => {
  const [allPathNames, setAllPathNames] = useState<string[]>([]);
  const [allPaths, setAllPaths] = useState<PathSummary[]>([]);
  const [isPathsLoading, setIsPathsLoading] = useState(false);
  const [isPathsLoaded, setIsPathsLoaded] = useState(false);

  const loadPathsAsync = useCallback(
    async (forceRefresh = false): Promise<string[]> => {
      if (isPathsLoaded && !forceRefresh) {
        return allPathNames;
      }

      setIsPathsLoading(true);
      try {
        const response = await fetchPathsFromApi({ withDuration: true });
        const allPathItems = [...(response.defaultPaths ?? []), ...(response.paths ?? [])];
        const pathNames = [
          ...allPathItems.map(p => p.name),
          ...(response.quests ?? []).map((q: { name: string }) => q.name),
        ];
        setAllPathNames(pathNames);
        const mappedPaths: PathSummary[] = allPathItems
          .filter((p): p is typeof p & { id: string | number } => p.id != null)
          .map(({ id, name, rankName, activated, totalPoints }) => ({
            id,
            name,
            rankName: rankName ?? "",
            activated: activated ?? false,
            totalPoints: totalPoints ?? 0,
          }));
        setAllPaths(mappedPaths);
        setIsPathsLoaded(true);
        return pathNames;
      } catch (error) {
        console.error("Failed to load paths:", error);
        throw error;
      } finally {
        setIsPathsLoading(false);
      }
    },
    [isPathsLoaded, allPathNames]
  );

  const invalidatePathsCache = useCallback(() => {
    setAllPathNames([]);
    setAllPaths([]);
    setIsPathsLoaded(false);
  }, []);

  const updatePathActivation = useCallback((id: string | number, activated: boolean) => {
    setAllPaths(prev => prev.map(p => (String(p.id) === String(id) ? { ...p, activated } : p)));
  }, []);

  return (
    <PathsContext.Provider
      value={{
        allPathNames,
        allPaths,
        isPathsLoading,
        isPathsLoaded,
        loadPathsAsync,
        invalidatePathsCache,
        updatePathActivation,
      }}
    >
      {children}
    </PathsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const usePathsContext = () => useContext(PathsContext);
